import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Copy,
  Download,
  FileSignature,
  List,
  MessageSquare,
  MoreVertical,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProspectBadge } from '@/components/shared/StatusBadges'
import { LoadingOverlay, computeChargesTotal } from '@/components/shared'
import { SendQuoteDialog } from '@/components/billing/SendQuoteDialog'
import { DeleteQuoteDialog } from '@/components/billing/DeleteQuoteDialog'
import { QuoteAttachments } from '@/components/billing/QuoteAttachments'
import {
  useQuote,
  useQuoteHistory,
  useSendQuote,
  useUpdateQuote,
} from '@/hooks/useQuotes'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { usePermissions } from '@/hooks/usePermissions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { quotesApi } from '@/services/quotes'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format'
import { DEFAULT_CATALOG_IMAGE_URL } from '@/lib/catalogImages'
import { computeDocumentTotals } from '@/lib/documentTotals'
import { defaultFeeImageUrl } from '@/lib/optionalFeeIcons'
import { primaryEmail } from '@/lib/clientContacts'
import { cn } from '@/lib/utils'
import {
  portalQuoteStatus,
  quoteHistoryVisualTone,
  quoteNextStep,
  quoteSentDate,
  quoteTerminalStatusDate,
  type PortalQuoteStatus,
} from '@/lib/quotes'
import {
  type ApiList,
  QUOTE_STATUS,
  type Quote,
  type QuoteDetail,
  type QuoteHistory,
} from '@/types'
import lifeOfQuoteArrowRightIcon from '@/assets/quotes/life-of-quote/arrow-right.svg'
import lifeOfQuoteCheckIcon from '@/assets/quotes/life-of-quote/check.svg'
import lifeOfQuoteLoaderIcon from '@/assets/quotes/life-of-quote/loader.svg'

const PAGE_CARD_CLASS =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_4px_4px_-4px_rgba(38,45,66,0.05),0_16px_32px_-4px_rgba(38,45,66,0.1)]'

const HERO_CARD_CLASS =
  'rounded-2xl border border-[#e7e9ef] bg-[linear-gradient(90deg,#ffffff_0%,#fffcfa_30%,#fff8f4_65%,#fff3ec_100%)] shadow-[0_4px_4px_-4px_rgba(38,45,66,0.05),0_16px_32px_-4px_rgba(38,45,66,0.1)]'

const OUTLINE_BUTTON_CLASS =
  'h-10 rounded-xl border border-[#e7e9ef] bg-white px-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#fbfbfc]'

const PRIMARY_BUTTON_CLASS =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const ICON_BUTTON_CLASS =
  'h-10 w-10 rounded-xl border border-transparent bg-white p-0 text-[#ff5a1f] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'

const TAB_LIST_CLASS =
  'h-auto w-full justify-start gap-1 rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_4px_4px_-4px_rgba(38,45,66,0.05),0_16px_32px_-4px_rgba(38,45,66,0.1)]'

const TAB_TRIGGER_CLASS =
  'rounded-lg border-0 px-3 py-1.5 text-sm font-medium leading-[1.4] text-[#3d434e] data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white'

const TH_CLASS =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium leading-[14px] tracking-normal text-[#3d434e]'

const TD_CLASS =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm leading-[1.4] text-[#171a20]'

const FRACTION_DIGITS = 2

type MetricTone = 'neutral' | 'warning' | 'amber' | 'success' | 'danger'
type StepTone = 'complete' | 'active' | 'upcoming' | 'failed'

type QuoteLineTableRow = {
  key: string
  description: string
  detail?: string
  imageUrl: string | null
  isDefaultIcon?: boolean
  isOptional: boolean
  isDiscount?: boolean
  quantity: number
  unitPrice: number
  taxRate: number
  lineTotalInclVat: number
}

const PORTAL_STATUS_OPTIONS: readonly PortalQuoteStatus[] = [
  'draft',
  'sent',
  'won',
  'lost',
]

const PORTAL_STATUS_TONE: Record<
  PortalQuoteStatus,
  { bg: string; text: string; stroke: string }
> = {
  draft: { bg: '#f4f5f8', text: '#171a20', stroke: '#272c36' },
  sent: { bg: '#dbeafe', text: '#1d4ed8', stroke: '#2e5ce6' },
  won: { bg: '#d1fae5', text: '#065f46', stroke: '#10b981' },
  lost: { bg: '#fecaca', text: '#7f1d1d', stroke: '#d63b33' },
}

function SurfaceCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn(PAGE_CARD_CLASS, className)}>{children}</section>
  )
}

function CardHeader({
  title,
  icon,
  className,
  iconWrapClassName,
}: {
  title: string
  icon: ReactNode
  className?: string
  iconWrapClassName?: string
}) {
  return (
    <div className={cn('flex items-center gap-3 px-6 pb-2 pt-6', className)}>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fbf3d9] text-[#ce7e0a]',
          iconWrapClassName
        )}
      >
        {icon}
      </span>
      <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
        {title}
      </h2>
    </div>
  )
}

function ClientAvatar({ name }: { name: string }) {
  const initials = useMemo(() => {
    const cleaned = name.trim()
    if (!cleaned) return 'Q'
    const words = cleaned.split(/\s+/).filter(Boolean)
    return words
      .slice(0, 2)
      .map(word => word[0]?.toUpperCase() ?? '')
      .join('')
  }, [name])

  return (
    <div className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#ff5a1f] font-heading text-base font-semibold leading-[1.2] text-white'>
      {initials}
    </div>
  )
}

function StatusChip({ status }: { status: Quote['status'] }) {
  const { t } = useTranslation('quotes')
  const portalStatus = portalQuoteStatus(status)
  const tone = PORTAL_STATUS_TONE[portalStatus]

  return (
    <span
      className='inline-flex h-6 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium leading-4'
      style={{ backgroundColor: tone.bg, color: tone.text }}
    >
      {t(`list.status.${portalStatus}`)}
      <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden>
        <path
          d='M3.5 5.25L7 8.75L10.5 5.25'
          stroke={tone.stroke}
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </span>
  )
}

function StatusDropdown({
  status,
  disabled,
  busy,
  onChange,
}: {
  status: Quote['status']
  disabled?: boolean
  busy?: boolean
  onChange: (status: PortalQuoteStatus) => void
}) {
  const { t } = useTranslation('quotes')
  const portalStatus = portalQuoteStatus(status)
  const tone = PORTAL_STATUS_TONE[portalStatus]

  if (disabled) {
    return <StatusChip status={status} />
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          disabled={busy}
          className='inline-flex h-6 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium leading-4 disabled:cursor-wait disabled:opacity-70'
          style={{ backgroundColor: tone.bg, color: tone.text }}
        >
          {t(`list.status.${portalStatus}`)}
          <svg
            width='14'
            height='14'
            viewBox='0 0 14 14'
            fill='none'
            aria-hidden
          >
            <path
              d='M3.5 5.25L7 8.75L10.5 5.25'
              stroke={tone.stroke}
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        sideOffset={8}
        className='w-40 rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_4px_4px_-1px_rgba(38,45,66,0.05),0_4px_8px_-1px_rgba(38,45,66,0.1)]'
      >
        {PORTAL_STATUS_OPTIONS.map(option => {
          const optionTone = PORTAL_STATUS_TONE[option]
          const selected = option === portalStatus

          return (
            <DropdownMenuItem
              key={option}
              className={cn(
                'rounded-xl border px-4 py-2 text-sm font-medium leading-[1.4] text-[#171a20] focus:text-[#171a20]',
                selected
                  ? 'border-[#ff5a1f] bg-[#fff1ea] focus:bg-[#fff1ea]'
                  : 'border-transparent bg-white focus:bg-[#f4f5f8]'
              )}
              onClick={() => onChange(option)}
            >
              <span
                className='size-2 rounded-full'
                style={{ backgroundColor: optionTone.stroke }}
                aria-hidden
              />
              <span>{t(`list.status.${option}`)}</span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MetricCard({
  eyebrow,
  title,
  description,
  tone = 'neutral',
  onClick,
}: {
  eyebrow: string
  title: string
  description?: string
  tone?: MetricTone
  onClick?: () => void
}) {
  const tones: Record<
    MetricTone,
    { bg: string; border: string; title: string; arrow: string }
  > = {
    neutral: {
      bg: '#fbfbfc',
      border: '#e7e9ef',
      title: '#171a20',
      arrow: '#171a20',
    },
    warning: {
      bg: '#fff1ea',
      border: '#ff9d75',
      title: '#ff5a1f',
      arrow: '#ff5a1f',
    },
    amber: {
      bg: '#fbf3d9',
      border: '#fde68a',
      title: '#a16207',
      arrow: '#a16207',
    },
    success: {
      bg: '#ecfdf5',
      border: '#a7f3d0',
      title: '#059669',
      arrow: '#059669',
    },
    danger: {
      bg: '#fde9e8',
      border: '#fca5a5',
      title: '#991b1b',
      arrow: '#b91c1c',
    },
  }
  const colors = tones[tone]

  return (
    <button
      type='button'
      disabled={!onClick}
      onClick={onClick}
      className='min-h-[96px] rounded-2xl border p-4 text-left shadow-[0_4px_4px_-4px_rgba(38,45,66,0.05),0_16px_32px_-4px_rgba(38,45,66,0.1)] disabled:cursor-default'
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <p className='font-heading text-xs font-medium leading-4 text-[#5a5a5a]'>
        {eyebrow}
      </p>
      <div className='mt-1.5 flex items-center gap-2'>
        <span
          className='font-heading text-[20px] font-bold leading-[1.2] tracking-[-0.02em]'
          style={{ color: colors.title }}
        >
          {title}
        </span>
        {onClick && (
          <ArrowRight
            className='size-4 shrink-0'
            style={{ color: colors.arrow }}
            strokeWidth={2.5}
          />
        )}
      </div>
      {description && (
        <p className='mt-1 text-xs font-medium leading-4 text-[#5a5a5a]'>
          {description}
        </p>
      )}
    </button>
  )
}

type HistoryTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral'
type HistoryPortalStatus = NonNullable<QuoteHistory['portal_status']>

const HISTORY_STATUS_TO_PORTAL_STATUS: Record<
  HistoryPortalStatus,
  PortalQuoteStatus
> = {
  Draft: 'draft',
  Sent: 'sent',
  Won: 'won',
  Lost: 'lost',
}

function historyTone(item: QuoteHistory): HistoryTone {
  return quoteHistoryVisualTone(item)
}

function historyVisual(item: QuoteHistory): {
  bgClassName: string
  iconClassName: string
  icon: ReactNode
} {
  const tone = historyTone(item)
  const eventType = item.event_type

  if (eventType === 'quote.sent') {
    return {
      bgClassName: 'bg-[#dbeafe]',
      iconClassName: 'text-[#2550cc]',
      icon: <Send className='size-4' strokeWidth={2.2} />,
    }
  }

  if (eventType === 'quote.created') {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <CalendarDays className='size-4' strokeWidth={2.2} />,
    }
  }

  if (eventType === 'quote.updated' && tone === 'warning') {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <Pencil className='size-4' strokeWidth={2.2} />,
    }
  }

  if (eventType === 'quote.duplicated') {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <Copy className='size-4' strokeWidth={2.2} />,
    }
  }

  if (tone === 'success') {
    return {
      bgClassName: 'bg-[#d1fae5]',
      iconClassName: 'text-[#059669]',
      icon: <Check className='size-4' strokeWidth={2.4} />,
    }
  }

  if (tone === 'danger') {
    return {
      bgClassName: 'bg-[#fde9e8]',
      iconClassName: 'text-[#d63b33]',
      icon: <Trash2 className='size-4' strokeWidth={2.2} />,
    }
  }

  return {
    bgClassName: 'bg-[#f4f5f8]',
    iconClassName: 'text-[#6c7285]',
    icon: <Clock3 className='size-4' strokeWidth={2.2} />,
  }
}

function parseHistoryRecipient(description: string | null): string | null {
  if (!description) return null
  const matched = description.match(/quote sent to\s+(.+)$/i)
  return matched?.[1]?.trim() || null
}

function latestSentRecipient(historyItems: QuoteHistory[]): string | null {
  const sent = historyItems.find(item => item.event_type === 'quote.sent')
  return sent ? parseHistoryRecipient(sent.description) : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeHistoryStatus(value: unknown): HistoryPortalStatus | null {
  if (typeof value !== 'string') return null

  switch (value.toLowerCase()) {
    case 'draft':
    case 'new':
    case 'suspended':
      return 'Draft'
    case 'sent':
    case 'expired':
      return 'Sent'
    case 'signed':
    case 'won':
    case 'converted':
      return 'Won'
    case 'declined':
    case 'lost':
      return 'Lost'
    default:
      return null
  }
}

function historyStatusFromProperties(
  item: QuoteHistory
): HistoryPortalStatus | null {
  const properties = item.properties
  if (!properties) return null

  const directStatus = normalizeHistoryStatus(properties.status)
  if (directStatus) return directStatus

  const changes = properties.changes
  if (!isRecord(changes)) return null

  const statusChange = changes.status
  if (!isRecord(statusChange)) return null

  return normalizeHistoryStatus(
    statusChange.new ?? statusChange.to ?? statusChange.after
  )
}

function historyStatusLabel(
  status: HistoryPortalStatus,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  return t(`list.status.${HISTORY_STATUS_TO_PORTAL_STATUS[status]}`)
}

function historyEventTitle(
  item: QuoteHistory,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const explicitStatus = historyStatusFromProperties(item)
  const eventStatus = explicitStatus ?? item.portal_status

  switch (item.event_type) {
    case 'quote.created':
      return t('detail.history.events.created')
    case 'quote.updated':
      if (explicitStatus) {
        return t('detail.history.events.statusChanged', {
          status: historyStatusLabel(explicitStatus, t),
        })
      }
      return t('detail.history.events.updated')
    case 'quote.sent':
      return t('detail.history.events.sent')
    case 'quote.signed':
      return t('detail.history.events.signedStatus', {
        status: historyStatusLabel(eventStatus ?? 'Won', t),
      })
    case 'quote.accepted':
      return t('detail.history.events.acceptedStatus', {
        status: historyStatusLabel(eventStatus ?? 'Won', t),
      })
    case 'quote.terminated':
      return t('detail.history.events.terminatedStatus', {
        status: historyStatusLabel(eventStatus ?? 'Lost', t),
      })
    case 'quote.duplicated':
      return t('detail.history.events.duplicated')
    case 'quote.converted_invoice':
      return t('detail.history.events.convertedInvoice')
    case 'quote.converted_contract':
      return t('detail.history.events.convertedContract')
    case 'quote.deleted':
      return t('detail.history.events.deleted')
    default:
      return (
        item.description?.trim() ||
        item.event_type.replace(/^quote\./, '').replace(/_/g, ' ')
      )
  }
}

function historyEventDetail({
  item,
  quoteTotal,
  fallbackRecipient,
  t,
}: {
  item: QuoteHistory
  quoteTotal: number
  fallbackRecipient: string | null
  t: (key: string, opts?: Record<string, unknown>) => string
}): string | null {
  switch (item.event_type) {
    case 'quote.created':
      return t('detail.history.notes.totalInclVat', {
        amount: formatCurrency(quoteTotal, 'EUR', FRACTION_DIGITS, 'suffix'),
      })
    case 'quote.sent':
      return (
        parseHistoryRecipient(item.description) ?? fallbackRecipient ?? null
      )
    case 'quote.signed':
      return t('detail.history.notes.signed')
    case 'quote.accepted':
      return t('detail.history.notes.accepted')
    case 'quote.terminated':
      return t('detail.history.notes.terminated')
    case 'quote.converted_invoice':
      return t('detail.history.notes.convertedInvoice')
    case 'quote.converted_contract':
      return t('detail.history.notes.convertedContract')
    default:
      return null
  }
}

function HistoryRow({
  item,
  quoteTotal,
  fallbackRecipient,
}: {
  item: QuoteHistory
  quoteTotal: number
  fallbackRecipient: string | null
}) {
  const { t } = useTranslation('quotes')
  const visual = historyVisual(item)
  const title = historyEventTitle(item, t)
  const primaryDetail = historyEventDetail({
    item,
    quoteTotal,
    fallbackRecipient,
    t,
  })
  const fallbackDetailParts = [
    item.user ? t('detail.history.meta.byUser', { name: item.user }) : null,
    item.portal_status
      ? t('detail.history.meta.status', { status: item.portal_status })
      : null,
    item.channel && item.channel !== 'System'
      ? t('detail.history.meta.channel', { channel: item.channel })
      : null,
  ].filter(Boolean)
  const fallbackDetail =
    fallbackDetailParts.join(' • ') ||
    (item.description && item.description.trim() !== title
      ? item.description
      : null)
  const detail = primaryDetail ?? fallbackDetail

  return (
    <div className='flex items-start gap-3 rounded-xl border border-[#e7e9ef] bg-white p-3'>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          visual.bgClassName,
          visual.iconClassName
        )}
      >
        {visual.icon}
      </span>

      <div className='min-w-0 flex-1'>
        <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
          {title}
        </p>
        <div className='mt-1 flex flex-wrap items-center gap-2 text-sm font-normal leading-[1.4] text-[#3d434e]'>
          <span>{formatDateTime(item.timestamp)}</span>
          {detail ? (
            <>
              <span className='size-1 rounded-full bg-[#8f95a6]' aria-hidden />
              <span className='min-w-0'>{detail}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function HistoryPanel({
  data,
  isLoading,
  error,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  quoteTotal,
  fallbackRecipient,
  loadingText,
  errorText,
  empty,
}: {
  data: ApiList<QuoteHistory> | undefined
  isLoading: boolean
  error: boolean
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  quoteTotal: number
  fallbackRecipient: string | null
  loadingText: string
  errorText: string
  empty: string
}) {
  const { t } = useTranslation('quotes')
  const items = data?.data ?? []
  const hasPagination = (data?.metadata?.total ?? 0) > pageSize

  if (isLoading) {
    return (
      <SurfaceCard className='overflow-hidden'>
        <CardHeader
          title={t('detail.history.title')}
          icon={<Clock3 className='size-4' strokeWidth={2.2} />}
          className='pb-4'
          iconWrapClassName='bg-[#eef2ff] text-[#4f6ef7]'
        />
        <div className='flex flex-col gap-2 px-4 pb-4 sm:px-6'>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className='h-[70px] rounded-xl' />
          ))}
          <p className='pt-2 text-sm text-[#3d434e]'>{loadingText}</p>
        </div>
      </SurfaceCard>
    )
  }

  if (error) {
    return (
      <SurfaceCard className='overflow-hidden'>
        <CardHeader
          title={t('detail.history.title')}
          icon={<Clock3 className='size-4' strokeWidth={2.2} />}
          className='pb-4'
          iconWrapClassName='bg-[#eef2ff] text-[#4f6ef7]'
        />
        <p className='px-6 pb-6 pt-2 text-sm text-destructive'>{errorText}</p>
      </SurfaceCard>
    )
  }

  if (items.length === 0) {
    return (
      <SurfaceCard className='overflow-hidden'>
        <CardHeader
          title={t('detail.history.title')}
          icon={<Clock3 className='size-4' strokeWidth={2.2} />}
          className='pb-4'
          iconWrapClassName='bg-[#eef2ff] text-[#4f6ef7]'
        />
        <p className='px-6 pb-6 pt-2 text-sm text-[#3d434e]'>{empty}</p>
      </SurfaceCard>
    )
  }

  return (
    <SurfaceCard className='overflow-hidden'>
      <CardHeader
        title={t('detail.history.title')}
        icon={<Clock3 className='size-4' strokeWidth={2.2} />}
        className='pb-4'
        iconWrapClassName='bg-[#eef2ff] text-[#4f6ef7]'
      />

      <div className='flex flex-col gap-2 px-4 pb-4 sm:px-6'>
        {items.map(item => (
          <HistoryRow
            key={item.id}
            item={item}
            quoteTotal={quoteTotal}
            fallbackRecipient={fallbackRecipient}
          />
        ))}
      </div>

      {hasPagination ? (
        <ListPagination
          className='px-4 pr-6'
          metadata={data?.metadata}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showShowingRange={false}
        />
      ) : null}
    </SurfaceCard>
  )
}

function parseInternalNoteEntries(notes: string) {
  return notes
    .split(/\n\s*\n/g)
    .map(entry => entry.trim())
    .filter(Boolean)
}

function TimelineStep({
  label,
  detail,
  state,
  nextConnector,
  previousConnector,
  onClick,
}: {
  label: string
  detail?: string | string[]
  state: StepTone
  nextConnector?: 'complete' | 'pending'
  previousConnector?: 'complete' | 'pending'
  onClick?: () => void
}) {
  const tone = {
    complete: {
      circleClass: 'bg-[#10b981] text-white border-[#10b981]',
      connectorClass: 'bg-[#10b981]',
      labelClass: 'text-[#171a20]',
      detailClass: 'text-[#3d434e]',
      icon: (
        <img
          src={lifeOfQuoteCheckIcon}
          alt=''
          aria-hidden
          className='size-3.5'
        />
      ),
    },
    active: {
      circleClass: 'bg-[#ff5a1f] text-white border-[#ff5a1f]',
      connectorClass: 'bg-[#e7e9ef]',
      labelClass: 'text-[#ff5a1f]',
      detailClass: 'text-[#3d434e]',
      icon: <span className='size-3 rounded-full bg-white' aria-hidden />,
    },
    upcoming: {
      circleClass: 'border border-[#b0b5c3] bg-white',
      connectorClass: 'bg-[#e7e9ef]',
      labelClass: 'text-[#3d434e]',
      detailClass: 'text-[#3d434e]',
      icon: null,
    },
    failed: {
      circleClass: 'bg-[#d63b33] text-white border-[#d63b33]',
      connectorClass: 'bg-[#10b981]',
      labelClass: 'text-[#b91c1c]',
      detailClass: 'text-[#3d434e]',
      icon: <X className='size-3.5' strokeWidth={2.2} />,
    },
  }[state]
  const detailLines = Array.isArray(detail)
    ? detail.filter(Boolean)
    : detail
      ? [detail]
      : []
  const interactive = typeof onClick === 'function'

  const content = (
    <>
      <div className='relative h-6 w-full'>
        {previousConnector ? (
          <span
            className={cn(
              'absolute left-0 right-[calc(50%+12px)] top-1/2 h-0.5 -translate-y-1/2 rounded-full',
              'lg:right-[calc(50%+20px)]',
              previousConnector === 'complete' ? 'bg-[#10b981]' : 'bg-[#e7e9ef]'
            )}
          />
        ) : null}
        <span
          className={cn(
            'absolute left-1/2 top-0 flex size-6 -translate-x-1/2 items-center justify-center rounded-full',
            tone.circleClass,
            interactive && 'transition-transform group-hover:scale-105'
          )}
        >
          {tone.icon}
        </span>
        {nextConnector ? (
          <span
            className={cn(
              'absolute left-[calc(50%+12px)] right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full',
              'lg:left-[calc(50%+20px)]',
              nextConnector === 'complete' ? 'bg-[#10b981]' : 'bg-[#e7e9ef]'
            )}
          />
        ) : (
          <span className='absolute left-[calc(50%+12px)] right-0 top-1/2 h-0.5 -translate-y-1/2 bg-transparent' />
        )}
      </div>
      <div className='mt-3 flex flex-col items-center gap-1 text-center'>
        <div className='flex items-center justify-center gap-1'>
          <span
            className={cn(
              'text-sm font-medium leading-[1.4]',
              tone.labelClass,
              interactive && 'group-hover:underline'
            )}
          >
            {label}
          </span>
          {interactive && state === 'active' ? (
            <img
              src={lifeOfQuoteArrowRightIcon}
              alt=''
              aria-hidden
              className='size-4 shrink-0'
            />
          ) : null}
        </div>
        {detailLines.map((line, index) => (
          <p
            key={`${label}-detail-${index}`}
            className={cn('text-xs font-medium leading-4', tone.detailClass)}
          >
            {line}
          </p>
        ))}
      </div>
    </>
  )

  if (interactive) {
    return (
      <button
        type='button'
        onClick={onClick}
        className='group flex min-w-0 flex-1 flex-col items-center rounded-xl text-center transition-colors'
      >
        {content}
      </button>
    )
  }

  return (
    <div className='flex min-w-0 flex-1 flex-col items-center'>{content}</div>
  )
}

function LifeOfQuoteCard({
  quote,
  historyItems = [],
  onSendAction,
  onInvoiceAction,
}: {
  quote: QuoteDetail
  historyItems?: QuoteHistory[]
  onSendAction?: () => void
  onInvoiceAction?: () => void
}) {
  const { t } = useTranslation('quotes')
  const clientEmail = quote.client?.email ?? primaryEmail(quote.client)
  const sentRecipient =
    quote.lifecycle?.sent_recipient ??
    latestSentRecipient(historyItems) ??
    clientEmail
  const portalStatus = portalQuoteStatus(quote.status)
  const sentAt = quoteSentDate(quote, historyItems)
  const terminalAt = quoteTerminalStatusDate(quote, historyItems)
  const daysRemaining = quote.valid_until
    ? Math.ceil(
        (new Date(`${quote.valid_until}T00:00:00`).getTime() -
          new Date().setHours(0, 0, 0, 0)) /
          86_400_000
      )
    : null

  const steps = (() => {
    if (portalStatus === 'draft') {
      return [
        {
          label: t('detail.timeline.created'),
          detail: quote.created_at ? formatDate(quote.created_at) : undefined,
          state: 'complete' as const,
          connector: 'complete' as const,
        },
        {
          label: t('detail.timeline.toSendAction'),
          detail: undefined,
          state: 'active' as const,
          connector: 'pending' as const,
          onClick: onSendAction,
        },
        {
          label: t('detail.timeline.clientResponse'),
          detail: undefined,
          state: 'upcoming' as const,
          connector: 'pending' as const,
        },
        {
          label: t('detail.timeline.billing'),
          detail: undefined,
          state: 'upcoming' as const,
        },
      ]
    }

    if (portalStatus === 'sent') {
      return [
        {
          label: t('detail.timeline.created'),
          detail: quote.created_at ? formatDate(quote.created_at) : undefined,
          state: 'complete' as const,
          connector: 'complete' as const,
        },
        {
          label: t('detail.timeline.sent'),
          detail: [sentAt ? formatDate(sentAt) : null, sentRecipient].filter(
            Boolean
          ) as string[],
          state: 'complete' as const,
          connector: 'complete' as const,
        },
        {
          label: t('detail.timeline.clientResponseWaiting'),
          detail:
            daysRemaining == null
              ? t('detail.timeline.waitingForResponse')
              : daysRemaining < 0
                ? t('detail.timeline.expired')
                : t('detail.timeline.validForDays', { count: daysRemaining }),
          state: 'active' as const,
          connector: 'pending' as const,
          onClick: onSendAction,
        },
        {
          label: t('detail.timeline.billing'),
          detail: undefined,
          state: 'upcoming' as const,
        },
      ]
    }

    if (portalStatus === 'lost') {
      return [
        {
          label: t('detail.timeline.created'),
          detail: quote.created_at ? formatDate(quote.created_at) : undefined,
          state: 'complete' as const,
          connector: 'complete' as const,
        },
        {
          label: t('detail.timeline.sent'),
          detail: [sentAt ? formatDate(sentAt) : null, sentRecipient].filter(
            Boolean
          ) as string[],
          state: 'complete' as const,
          connector: 'complete' as const,
        },
        {
          label: t('detail.timeline.rejected'),
          detail: terminalAt
            ? formatDate(terminalAt)
            : t('detail.timeline.rejectedHint'),
          state: 'failed' as const,
          onClick: onSendAction,
        },
      ]
    }

    return [
      {
        label: t('detail.timeline.created'),
        detail: quote.created_at ? formatDate(quote.created_at) : undefined,
        state: 'complete' as const,
        connector: 'complete' as const,
      },
      {
        label: t('detail.timeline.sent'),
        detail: [sentAt ? formatDate(sentAt) : null, sentRecipient].filter(
          Boolean
        ) as string[],
        state: 'complete' as const,
        connector: 'complete' as const,
      },
      {
        label: t('detail.timeline.clientResponse'),
        detail: terminalAt ? formatDate(terminalAt) : undefined,
        state: 'complete' as const,
        connector: quote.converted_invoice_id
          ? ('complete' as const)
          : ('pending' as const),
      },
      {
        label: quote.converted_invoice_id
          ? t('detail.timeline.invoiceReady')
          : t('detail.timeline.toInvoice'),
        detail: quote.converted_at ? formatDate(quote.converted_at) : undefined,
        state: quote.converted_invoice_id
          ? ('complete' as const)
          : ('active' as const),
        onClick: onInvoiceAction,
      },
    ]
  })()

  return (
    <SurfaceCard className='overflow-hidden shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'>
      <CardHeader
        title={t('detail.lifeOfQuote')}
        icon={
          <img
            src={lifeOfQuoteLoaderIcon}
            alt=''
            aria-hidden
            className='size-4'
          />
        }
        iconWrapClassName='bg-[#ecfdf5] text-[#059669]'
      />
      <div className='px-6 pb-6 pt-4'>
        <div className='flex flex-col gap-6 lg:flex-row lg:gap-0 lg:items-start'>
          {steps.map((step, index) => (
            <TimelineStep
              key={`${step.label}-${index}`}
              label={step.label}
              detail={step.detail}
              state={step.state}
              previousConnector={
                index > 0 ? steps[index - 1]?.connector : undefined
              }
              nextConnector={
                index < steps.length - 1 ? step.connector : undefined
              }
              onClick={step.onClick}
            />
          ))}
        </div>
      </div>
    </SurfaceCard>
  )
}

function QuoteLinesCard({
  quote,
  surchargeTotal,
}: {
  quote: QuoteDetail
  surchargeTotal: number
}) {
  const { t } = useTranslation('quotes')
  const { t: ts } = useTranslation('shared')
  const fmt = (value: number) => formatCurrency(value, 'EUR', FRACTION_DIGITS)
  const surcharges = quote.surcharges ?? []
  const discountAmount = quote.discount_amount ?? 0
  const totalExcludingVat =
    Math.round((quote.subtotal + surchargeTotal - discountAmount) * 100) / 100
  const vatRows = computeDocumentTotals(
    quote.items ?? [],
    surcharges,
    quote.discount_type && (quote.discount_value ?? 0) > 0
      ? {
          type: quote.discount_type,
          value: quote.discount_value,
        }
      : null
  ).tax_breakdown.filter(row => row.tax > 0)
  const lineRows: QuoteLineTableRow[] = [
    ...(quote.items ?? []).map((item, index) => ({
      key: item.id ?? `line-${index}`,
      description: item.description,
      detail: item.service_description?.trim()
        ? item.service_description
        : undefined,
      imageUrl: item.image_url ?? DEFAULT_CATALOG_IMAGE_URL,
      isOptional: false,
      quantity: item.quantity,
      unitPrice: item.unit_price ?? 0,
      taxRate: item.tax_rate,
      lineTotalInclVat: item.line_total * (1 + item.tax_rate / 100),
    })),
    ...surcharges.map((surcharge, index) => {
      const description = surcharge.description?.trim()
      const taxRate = surcharge.tax_rate ?? 0

      return {
        key: surcharge.id ?? `optional-${index}`,
        description:
          description || ts(`optionalCharges.types.${surcharge.type}`),
        imageUrl: surcharge.image_url || defaultFeeImageUrl(surcharge.type),
        isDefaultIcon: !surcharge.image_url,
        isOptional: true,
        quantity: 1,
        unitPrice: surcharge.amount,
        taxRate,
        lineTotalInclVat: surcharge.amount * (1 + taxRate / 100),
      }
    }),
    ...(discountAmount > 0
      ? [
          {
            key: 'trade-discount',
            description: t('form.tradeDiscountCard.rowTitle'),
            detail: t('form.tradeDiscountCard.rowDescription'),
            imageUrl: null,
            isOptional: false,
            isDiscount: true,
            quantity: 0,
            unitPrice: 0,
            taxRate: 0,
            lineTotalInclVat: discountAmount,
          },
        ]
      : []),
  ]

  return (
    <SurfaceCard className='overflow-hidden'>
      <CardHeader
        title={t('detail.lineItems')}
        icon={<List className='size-4' />}
        iconWrapClassName='bg-[#fff1ea] text-[#ff5a1f]'
      />

      <div className='overflow-x-auto px-6 pb-0 pt-4'>
        <Table className='min-w-[976px] table-fixed'>
          <TableHeader>
            <TableRow>
              <TableHead className={cn(TH_CLASS, 'w-16 text-left')}>
                {t('detail.columns.image')}
              </TableHead>
              <TableHead className={TH_CLASS}>
                {t('detail.columns.service')}
              </TableHead>
              <TableHead className={cn(TH_CLASS, 'w-40 text-center 2xl:w-60')}>
                {t('detail.columns.quantity')}
              </TableHead>
              <TableHead className={cn(TH_CLASS, 'w-40 text-center 2xl:w-60')}>
                {t('detail.columns.unitPrice')}
              </TableHead>
              <TableHead className={cn(TH_CLASS, 'w-40 text-center 2xl:w-60')}>
                {t('detail.columns.vatRate')}
              </TableHead>
              <TableHead className={cn(TH_CLASS, 'w-40 text-center 2xl:w-60')}>
                {t('detail.columns.lineTotal')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className='px-3 py-8 text-center text-sm text-[#3d434e]'
                >
                  {t('detail.noLineItems')}
                </TableCell>
              </TableRow>
            )}

            {lineRows.map(row => (
              <TableRow
                key={row.key}
                className={cn(row.isDiscount && 'bg-[#ecfdf5]')}
              >
                <TableCell
                  className={cn(TD_CLASS, row.isDiscount && 'bg-[#ecfdf5]')}
                >
                  {row.isDiscount ? (
                    <div className='flex size-12 items-center justify-center rounded-[6px] bg-white text-[#3d434e]'>
                      <Tag className='size-4' />
                    </div>
                  ) : row.imageUrl ? (
                    <div className='flex size-12 items-center justify-center overflow-hidden rounded-[6px] bg-[#fff1ea]'>
                      <img
                        src={row.imageUrl}
                        alt=''
                        className={
                          row.isDefaultIcon
                            ? 'size-4 object-contain'
                            : 'size-auto object-cover object-center'
                        }
                      />
                    </div>
                  ) : (
                    <div className='flex size-12 items-center justify-center overflow-hidden rounded-[6px] bg-[#fff1ea]'>
                      <img
                        src={DEFAULT_CATALOG_IMAGE_URL}
                        alt=''
                        className='size-4 object-contain'
                      />
                    </div>
                  )}
                </TableCell>
                <TableCell
                  className={cn(TD_CLASS, row.isDiscount && 'bg-[#ecfdf5]')}
                >
                  <div className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-2'>
                    <span
                      className={cn(
                        'min-w-0 whitespace-pre-wrap font-medium',
                        row.isDiscount && 'uppercase'
                      )}
                    >
                      {row.description}
                    </span>
                    {row.isOptional ? (
                      <span className='inline-flex shrink-0 translate-y-[0.5px] items-center self-center gap-1 text-xs font-normal leading-4 text-[#6c7285]'>
                        <span className='size-1 rounded-full bg-[#6c7285]' />
                        {t('detail.optionalFee')}
                      </span>
                    ) : null}
                    {row.detail ? (
                      <span className='basis-full whitespace-pre-wrap break-words text-xs font-normal leading-4 text-[#3d434e]'>
                        {row.detail}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell
                  className={cn(
                    TD_CLASS,
                    'text-center font-medium',
                    row.isDiscount && 'bg-[#ecfdf5]'
                  )}
                >
                  {row.isDiscount ? '-' : row.quantity}
                </TableCell>
                <TableCell
                  className={cn(
                    TD_CLASS,
                    'text-center font-medium',
                    row.isDiscount && 'bg-[#ecfdf5]'
                  )}
                >
                  {row.isDiscount ? '-' : fmt(row.unitPrice)}
                </TableCell>
                <TableCell
                  className={cn(
                    TD_CLASS,
                    'text-center font-medium',
                    row.isDiscount && 'bg-[#ecfdf5]'
                  )}
                >
                  {row.isDiscount ? '-' : `${row.taxRate}%`}
                </TableCell>
                <TableCell
                  className={cn(
                    TD_CLASS,
                    'text-center font-medium',
                    row.isDiscount && 'bg-[#ecfdf5] text-[#059669]'
                  )}
                >
                  {row.isDiscount ? '-' : ''}
                  {fmt(row.lineTotalInclVat)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className='flex justify-end px-6 pb-6 pt-10'>
        <div className='w-full max-w-[400px] space-y-4'>
          <div className='flex items-center justify-between gap-4 px-3 text-sm leading-[1.4]'>
            <span className='text-[#6c7285]'>{t('detail.subtotal')}</span>
            <span className='font-medium text-[#171a20]'>
              {fmt(quote.subtotal)}
            </span>
          </div>
          {surchargeTotal > 0 ? (
            <div className='flex items-center justify-between gap-4 px-3 text-sm leading-[1.4]'>
              <span className='text-[#6c7285]'>
                {t('detail.optionalFeesExcludingVat')}
              </span>
              <span className='font-medium text-[#171a20]'>
                {fmt(surchargeTotal)}
              </span>
            </div>
          ) : null}
          {discountAmount > 0 ? (
            <div className='flex items-center justify-between gap-4 px-3 text-sm leading-[1.4]'>
              <span className='text-[#059669]'>
                {t('form.tradeDiscountCard.rowTitle')}
              </span>
              <span className='font-medium text-[#059669]'>
                -{fmt(discountAmount)}
              </span>
            </div>
          ) : null}
          <div className='border-t border-[#e7e9ef] pt-4'>
            <div className='flex items-center justify-between gap-4 px-3 text-sm font-bold leading-[1.4] text-[#171a20]'>
              <span>{t('form.grandTotal.subtotal')}</span>
              <span>{fmt(totalExcludingVat)}</span>
            </div>
          </div>
          {vatRows.map(row => (
            <div
              key={row.rate}
              className='flex items-center justify-between gap-4 px-3 text-sm leading-[1.4]'
            >
              <span className='text-[#6c7285]'>
                {t('form.grandTotal.vatRateLabel', { rate: row.rate })}
              </span>
              <span className='font-medium text-[#171a20]'>{fmt(row.tax)}</span>
            </div>
          ))}
          <div className='flex items-center justify-between gap-4 rounded-xl bg-[#f4f5f8] p-3'>
            <span className='font-heading text-[20px] font-bold leading-[1.2] tracking-[-0.02em] text-[#171a20]'>
              {t('detail.totalInclVat')}
            </span>
            <span className='font-heading text-[20px] font-bold leading-[1.2] tracking-[-0.02em] text-[#171a20]'>
              {fmt(quote.total)}
            </span>
          </div>
        </div>
      </div>
    </SurfaceCard>
  )
}

function InternalNotesCard({
  canEdit,
  notes,
  draftNote,
  onDraftChange,
  onAdd,
  addDisabled,
  saving,
  updatedAt,
}: {
  canEdit: boolean
  notes: string
  draftNote: string
  onDraftChange: (value: string) => void
  onAdd: () => void
  addDisabled: boolean
  saving: boolean
  updatedAt?: string | null
}) {
  const { t } = useTranslation(['quotes', 'common'])
  const noteEntries = useMemo(() => parseInternalNoteEntries(notes), [notes])
  const noteMeta = updatedAt ? formatDateTime(updatedAt) : null

  return (
    <SurfaceCard className='flex h-full min-w-0 flex-col overflow-hidden shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'>
      <CardHeader
        title={t('detail.internalNotes')}
        icon={<MessageSquare className='size-4' strokeWidth={2.2} />}
        className='h-16 shrink-0 gap-2 [&>h2]:min-w-0 [&>h2]:flex-1 [&>h2]:text-sm'
        iconWrapClassName='bg-[#fbf3d9] p-2 text-[#e6a600]'
      />

      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='flex max-h-[336px] min-h-0 flex-col gap-2 overflow-y-auto px-6 pb-4 pt-4'>
          {noteEntries.length > 0 ? (
            <>
              {noteEntries.map((entry, index) => (
                <div
                  key={`${entry.slice(0, 24)}-${index}`}
                  className='flex flex-col gap-2 rounded-xl bg-[#f4f5f8] px-4 py-3'
                >
                  {noteMeta ? (
                    <p className='text-xs font-medium leading-4 text-[#3d434e]'>
                      {noteMeta}
                    </p>
                  ) : null}
                  <p className='whitespace-pre-wrap wrap-break-word text-sm font-normal leading-[1.4] text-[#171a20]'>
                    {entry}
                  </p>
                </div>
              ))}
            </>
          ) : (
            <p className='text-sm leading-[1.4] text-[#3d434e]'>
              {t('detail.emptyNotes')}
            </p>
          )}
        </div>

        {canEdit ? (
          <div className='mt-auto flex shrink-0 items-start gap-4 px-6 pb-6 pt-4'>
            <Input
              value={draftNote}
              placeholder={t('detail.notesPlaceholder')}
              onChange={event => onDraftChange(event.target.value)}
              className='h-10 flex-1 rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-2.5 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#6c7285] focus-visible:ring-[#ff5a1f]'
            />
            <Button
              type='button'
              variant='ghost'
              className='h-10 shrink-0 gap-2 rounded-xl px-3 py-3 text-sm font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
              disabled={addDisabled || saving}
              onClick={onAdd}
            >
              <Plus className='size-4' />
              {t('common:actions.add')}
            </Button>
          </div>
        ) : null}
      </div>
    </SurfaceCard>
  )
}

export default function QuoteDetailPage() {
  const { t } = useTranslation('quotes')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError, refetch } = useQuote(id)
  const updateQuote = useUpdateQuote(id ?? '')
  const sendQuote = useSendQuote(id ?? '')
  const {
    canDelete: canDeleteModule,
    canEdit: canEditModule,
    canViewPath,
  } = usePermissions()
  const quoteData = data?.data
  useBreadcrumbLabel(quoteData?.number ?? undefined)

  const [sendOpen, setSendOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Quote | null>(null)
  const [notesState, setNotesState] = useState<{
    quoteId: string | null
    value: string
  }>({
    quoteId: null,
    value: '',
  })
  const [noteDraftState, setNoteDraftState] = useState<{
    quoteId: string | null
    value: string
  }>({
    quoteId: null,
    value: '',
  })
  const [savingNotes, setSavingNotes] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [convertingInvoice, setConvertingInvoice] = useState(false)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyPageSize, setHistoryPageSize] = useState(20)
  const historyQuery = useMemo(
    () => ({ page: historyPage, limit: historyPageSize }),
    [historyPage, historyPageSize]
  )
  const history = useQuoteHistory(id, historyQuery)

  useEffect(() => {
    if (searchParams.get('send') !== '1') return

    // One-shot: open the send dialog after Save and Send, then drop ?send=1
    // so browser back/refresh does not reopen it unexpectedly.
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    setSendOpen(true)
    setSearchParams(
      current => {
        const next = new URLSearchParams(current)
        next.delete('send')
        return next
      },
      { replace: true }
    )
  }, [searchParams, setSearchParams])

  if (isLoading) {
    return (
      <div className='py-10 text-center text-muted-foreground'>
        {t('detail.loading')}
      </div>
    )
  }

  if (isError || !quoteData) {
    return (
      <div className='py-10 text-center'>
        <p className='text-destructive'>{t('detail.loadError')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => navigate('/quotes')}
        >
          {t('detail.backToQuotes')}
        </Button>
      </div>
    )
  }

  const quote = quoteData
  const notes =
    notesState.quoteId === quote.id ? notesState.value : (quote.notes ?? '')
  const noteDraft =
    noteDraftState.quoteId === quote.id ? noteDraftState.value : ''
  const portalStatus = portalQuoteStatus(quote.status)
  const hasConvertedInvoice = !!quote.converted_invoice_id
  const hasConvertedContract = !!quote.converted_contract_id
  const hasConverted = hasConvertedInvoice || hasConvertedContract
  const statusLocked = quote.status === QUOTE_STATUS.LOST || hasConverted
  const canConvert = quote.status === QUOTE_STATUS.WON && !hasConverted
  const canEdit = !statusLocked && canEditModule('quotes')
  const canManageNotes = canEditModule('quotes')
  const canDelete = !hasConverted && canDeleteModule('quotes')
  const canChangeStatus = !statusLocked && canEditModule('quotes')
  const canOpenInvoice =
    canEditModule('quotes') && (canConvert || hasConvertedInvoice)
  const showPrimaryAction =
    canEditModule('quotes') && (portalStatus !== 'won' || canOpenInvoice)
  const showConvertSection = canEditModule('quotes') && canConvert
  const showDeleteSection = canDelete
  const addNoteDisabled = !noteDraft.trim()
  const handleNoteDraftChange = (value: string) => {
    setNoteDraftState({
      quoteId: quote.id,
      value,
    })
  }
  const surchargeTotal =
    quote.surcharge_total ??
    computeChargesTotal(
      (quote.surcharges ?? []).map(surcharge => ({
        type: surcharge.type,
        description: surcharge.description ?? '',
        amount: surcharge.amount,
      }))
    )

  const clientName =
    quote.client?.name ?? quote.client_name ?? t('detail.viewClient')
  const canViewClient = canViewPath(`/clients/${quote.client_id}`)
  const quoteSubtitle = quote.title?.trim()
  const nextStep = quoteNextStep(quote)

  const exportPdf = async () => {
    setExportingPdf(true)
    try {
      const res = await quotesApi.pdf(quote.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.pdfError')),
      })
    } finally {
      setExportingPdf(false)
    }
  }

  const duplicate = async () => {
    try {
      const res = await quotesApi.duplicate(quote.id)
      invalidatePortalMetricQueries(queryClient)
      toast({ variant: 'success', title: t('detail.duplicateSuccess') })
      if (res.data?.id) navigate(`/quotes/${res.data.id}/edit`)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.duplicateError')),
      })
    }
  }

  const updatePortalStatus = async (nextPortalStatus: PortalQuoteStatus) => {
    const nextStatus = {
      draft: QUOTE_STATUS.DRAFT,
      sent: QUOTE_STATUS.SENT,
      won: QUOTE_STATUS.WON,
      lost: QUOTE_STATUS.LOST,
    }[nextPortalStatus]

    if (quote.status === nextStatus) return

    setUpdatingStatus(true)
    try {
      await updateQuote.mutateAsync({ status: nextStatus })
      toast({ variant: 'success', title: t('detail.statusSaveSuccess') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.statusSaveError')),
      })
    } finally {
      setUpdatingStatus(false)
    }
  }

  const addNote = async () => {
    if (!canManageNotes || !noteDraft.trim()) return
    setSavingNotes(true)
    try {
      const nextEntry = noteDraft.trim()
      const nextNotes = notes.trim()
        ? `${notes.trim()}\n\n${nextEntry}`
        : nextEntry
      await updateQuote.mutateAsync({ notes: nextNotes || undefined })
      setNotesState({
        quoteId: quote.id,
        value: nextNotes,
      })
      setNoteDraftState({
        quoteId: quote.id,
        value: '',
      })
      toast({ variant: 'success', title: t('detail.notesSaveSuccess') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.notesSaveError')),
      })
    } finally {
      setSavingNotes(false)
    }
  }

  const openOrCreateInvoice = async () => {
    if (quote.converted_invoice_id) {
      navigate(`/invoices/${quote.converted_invoice_id}`)
      return
    }

    setConvertingInvoice(true)
    try {
      const res = await quotesApi.convertToInvoice(quote.id)
      invalidatePortalMetricQueries(queryClient)
      toast({ variant: 'success', title: t('detail.convertInvoiceSuccess') })
      if (res.data?.id) navigate(`/invoices/${res.data.id}`)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.convertInvoiceError')),
      })
    } finally {
      setConvertingInvoice(false)
    }
  }

  const openOrCreateContract = () => {
    if (quote.converted_contract_id) {
      navigate(`/contracts/${quote.converted_contract_id}`)
      return
    }

    navigate(`/contracts/new?quote_id=${quote.id}&client_id=${quote.client_id}`)
  }

  const onNextStep = () => {
    if (nextStep.action === 'invoice') {
      void openOrCreateInvoice()
      return
    }

    setSendOpen(true)
  }

  const nextStepCard = (() => {
    if (portalStatus === 'sent') {
      const sentDaysRemaining =
        nextStep.action === 'resend' ? nextStep.daysRemaining : 0

      return {
        eyebrow: t('detail.nextStep.waitingForClient'),
        title:
          nextStep.action === 'resend' && nextStep.labelKey === 'expired'
            ? t('detail.nextStep.expired')
            : t('detail.nextStep.daysRemaining', {
                count: sentDaysRemaining,
              }),
        description: quote.valid_until
          ? t('detail.nextStep.expiresOn', {
              date: formatDate(quote.valid_until),
            })
          : t('detail.nextStep.descriptions.resend'),
        tone: 'amber' as const,
      }
    }

    if (portalStatus === 'won') {
      return {
        eyebrow: t('detail.nextStep.wonToInvoice'),
        title: formatCurrency(quote.total, 'EUR', 2, 'suffix'),
        description: t('detail.nextStep.invoiceHint'),
        tone: 'success' as const,
      }
    }

    if (portalStatus === 'lost') {
      return {
        eyebrow: t('detail.nextStep.denied'),
        title: t('detail.nextStep.retryRejected'),
        description: t('detail.nextStep.descriptions.resend_adjusted'),
        tone: 'danger' as const,
      }
    }

    return {
      eyebrow: t('detail.nextStep.title'),
      title: t('detail.nextStep.toSend'),
      description: t('detail.nextStep.draftHint'),
      tone: 'warning' as const,
    }
  })()

  const primaryActionLabel = (() => {
    if (portalStatus === 'won') return t('detail.convertToInvoice')
    if (portalStatus === 'lost') return t('detail.resendQuote')
    return t('detail.sendToClient')
  })()

  return (
    <div className='-m-6 flex min-h-full flex-col gap-4 bg-[#f4f5f8] p-6'>
      <section className={cn(HERO_CARD_CLASS, 'p-6')}>
        <div className='flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between'>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-start gap-3'>
              <ClientAvatar name={clientName} />
              <div className='min-w-0 flex-1'>
                <div className='flex flex-wrap items-center gap-2'>
                  {canViewClient ? (
                    <Link
                      to={`/clients/${quote.client_id}`}
                      className='line-clamp-1 font-heading text-[20px] font-semibold leading-[1.2] text-[#171a20] hover:underline'
                    >
                      {clientName}
                    </Link>
                  ) : (
                    <h1 className='line-clamp-1 font-heading text-[20px] font-semibold leading-[1.2] text-[#171a20]'>
                      {clientName}
                    </h1>
                  )}
                  <span className='inline-block px-1.5 py-1 bg-[#E7E9EF] rounded-lg text-xs font-medium leading-[16px] text-[#171A20]'>
                    {quote.number ?? t('detail.fallbackTitle')}
                  </span>
                  <StatusDropdown
                    status={quote.status}
                    disabled={!canChangeStatus}
                    busy={updatingStatus}
                    onChange={status => void updatePortalStatus(status)}
                  />
                  {quote.client?.category && (
                    <ProspectBadge category={quote.client.category} />
                  )}
                </div>
                {quoteSubtitle && (
                  <p className='mt-0.5 text-xs font-normal leading-[1.4] text-[#3d434e]'>
                    {quoteSubtitle}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-start gap-3 xl:justify-end'>
            <Button
              variant='outline'
              className={OUTLINE_BUTTON_CLASS}
              onClick={exportPdf}
            >
              <Download className='mr-2 size-4' />
              {t('detail.exportPdf')}
            </Button>

            {showPrimaryAction && (
              <Button
                className={PRIMARY_BUTTON_CLASS}
                disabled={convertingInvoice}
                onClick={onNextStep}
              >
                {nextStep.action === 'invoice' ? (
                  <RefreshCw className='mr-2 size-4' />
                ) : (
                  <Send className='mr-2 size-4' />
                )}
                {primaryActionLabel}
              </Button>
            )}

            <DropdownMenu>
              <TruncatedTextTooltip
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
                content={t('detail.moreActions')}
                delayDuration={0}
              >
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' className={ICON_BUTTON_CLASS}>
                    <MoreVertical className='size-4' />
                    <span className='sr-only'>{t('detail.moreActions')}</span>
                  </Button>
                </DropdownMenuTrigger>
              </TruncatedTextTooltip>
              <DropdownMenuContent
                align='end'
                className='w-56 rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_4px_4px_-1px_rgba(38,45,66,0.05),0_4px_8px_-1px_rgba(38,45,66,0.1)]'
              >
                {canEdit && (
                  <DropdownMenuItem
                    className='rounded-lg px-3 py-2 text-sm text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
                    onClick={() => navigate(`/quotes/${quote.id}/edit`)}
                  >
                    <Pencil className='mr-2 size-4' />
                    {t('detail.edit')}
                  </DropdownMenuItem>
                )}
                {canEditModule('quotes') && (
                  <DropdownMenuItem
                    className='rounded-lg px-3 py-2 text-sm text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
                    onClick={duplicate}
                  >
                    <Copy className='mr-2 size-4' />
                    {t('detail.duplicate')}
                  </DropdownMenuItem>
                )}
                {(canEdit || canEditModule('quotes')) && showConvertSection && (
                  <DropdownMenuSeparator className='mx-1 my-2 bg-[#e7e9ef]' />
                )}
                {showConvertSection && (
                  <DropdownMenuItem
                    className='rounded-lg px-3 py-2 text-sm text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
                    onClick={openOrCreateContract}
                  >
                    <FileSignature className='mr-2 size-4' />
                    {t('detail.convertToContract')}
                  </DropdownMenuItem>
                )}
                {(canEdit || showConvertSection) && showDeleteSection && (
                  <DropdownMenuSeparator className='mx-1 my-2 bg-[#e7e9ef]' />
                )}
                {canDelete && (
                  <DropdownMenuItem
                    className='rounded-lg px-3 py-2 text-sm text-[#d63b33] focus:bg-[#fde9e8] focus:text-[#d63b33]'
                    onClick={() => setDeleteTarget(quote)}
                  >
                    <Trash2 className='mr-2 size-4' />
                    {t('detail.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      <div className='grid gap-4 lg:grid-cols-2'>
        <MetricCard
          eyebrow={t('detail.totalAmount')}
          title={formatCurrency(quote.total, 'EUR', 2, 'suffix')}
          tone='neutral'
        />
        <MetricCard
          eyebrow={nextStepCard.eyebrow}
          title={nextStepCard.title}
          description={nextStepCard.description}
          tone={nextStepCard.tone}
          onClick={
            showPrimaryAction && !convertingInvoice ? onNextStep : undefined
          }
        />
      </div>

      <Tabs defaultValue='details' className='flex flex-col gap-4'>
        <TabsList className={TAB_LIST_CLASS}>
          <TabsTrigger value='details' className={TAB_TRIGGER_CLASS}>
            {t('detail.tabs.details')}
          </TabsTrigger>
          <TabsTrigger value='history' className={TAB_TRIGGER_CLASS}>
            {t('detail.tabs.history')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value='details' className='mt-0 flex flex-col gap-4'>
          <LifeOfQuoteCard
            quote={quote}
            historyItems={history.data?.data ?? []}
            onSendAction={
              showPrimaryAction ? () => setSendOpen(true) : undefined
            }
            onInvoiceAction={
              canOpenInvoice ? () => void openOrCreateInvoice() : undefined
            }
          />

          <QuoteLinesCard quote={quote} surchargeTotal={surchargeTotal} />

          <div className='grid auto-rows-2 items-stretch gap-4 xl:grid-cols-2'>
            <InternalNotesCard
              canEdit={canManageNotes}
              notes={notes}
              draftNote={noteDraft}
              onDraftChange={handleNoteDraftChange}
              onAdd={() => void addNote()}
              addDisabled={addNoteDisabled}
              saving={savingNotes}
              updatedAt={quote.updated_at}
            />

            <SurfaceCard className='flex h-full flex-col overflow-hidden'>
              <CardHeader
                title={t('detail.attachFiles')}
                icon={<Paperclip className='size-4' strokeWidth={2.2} />}
                iconWrapClassName='bg-[#fbf3d9] text-[#ce7e0a]'
              />
              <div className='min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4'>
                <QuoteAttachments
                  quoteId={quote.id}
                  clientId={quote.client_id}
                  hideHeading
                  readOnly={!canEditModule('quotes')}
                />
              </div>
            </SurfaceCard>
          </div>
        </TabsContent>

        <TabsContent value='history' className='mt-0'>
          <HistoryPanel
            data={history.data}
            isLoading={history.isLoading}
            error={history.isError}
            page={historyPage}
            pageSize={historyPageSize}
            onPageChange={setHistoryPage}
            onPageSizeChange={size => {
              setHistoryPageSize(size)
              setHistoryPage(1)
            }}
            quoteTotal={quote.total}
            fallbackRecipient={
              quote.client?.email ?? primaryEmail(quote.client) ?? null
            }
            loadingText={t('detail.history.loading')}
            errorText={t('detail.history.loadError')}
            empty={t('detail.history.empty')}
          />
        </TabsContent>
      </Tabs>

      <SendQuoteDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        quote={quote}
        total={quote.total}
        onSend={async body => {
          await sendQuote.mutateAsync(body)
        }}
        onSent={() => {
          void refetch()
        }}
      />
      <DeleteQuoteDialog
        quote={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        navigateOnDelete
      />
      <LoadingOverlay open={exportingPdf} />
    </div>
  )
}
